import { Maximize2Icon, Minimize2Icon } from "lucide-react";

import { Column } from "@/components/Column";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { DashboardHeaderBackgroundPicker } from "@/components/dashboard/DashboardHeaderBackgroundPicker";
import { DashboardUserSelector } from "@/components/dashboard/DashboardUserSelector";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { HousePennants } from "@/components/winter";

import { useTaggableUsers } from "@/contexts/TaggableUsersContext";
import { useGetCurrentUser } from "@/hooks/queries/useGetCurrentUser";
import { useGetUserSettings } from "@/hooks/queries/useGetUserSettings";
import { useUpdateSetUserSettings } from "@/hooks/updateSets/useUpdateSetUserSettings";
import {
  ALL_USERS_KEY,
  DASHBOARD_HEADER_HEIGHT_PX,
  DASHBOARD_HEADER_MINIMIZED_HEIGHT_PX,
  dashboardHeaderColorToBannerClassName,
} from "@/lib/constants";
import { dates } from "@/lib/dates";
import { cn } from "@/lib/utils";
import { DashboardHeaderColor, DashboardSelectedUser } from "@/types";

// Every gradient is dark, so foreground text/icons stay white.
const HEADLINE_CLASS = "text-white";
const SUBTITLE_CLASS = "text-white/70";
const ICON_BUTTON_CLASS = "text-white/60 hover:bg-white/10 hover:text-white/90";
const LAYER_CLASS =
  "absolute inset-0 items-center px-[27px] transition-opacity duration-300 ease-in-out";
const SELECTOR_TRIGGER_CLASS =
  "h-8 w-[134px] shrink-0 border-slate-300 bg-white px-3 text-[11px] font-semibold text-[#153226] hover:bg-white hover:text-[#153226] dark:text-slate-200 dark:hover:text-white";

export const DashboardHeader = ({
  // Owned by the page so the banner and stat row animate in lockstep.
  isMinimized,
  selectedUserId,
  onSelectedUserIdChange,
}: {
  isMinimized: boolean;
  selectedUserId: DashboardSelectedUser;
  onSelectedUserIdChange: (value: DashboardSelectedUser) => void;
}) => {
  /* Database CRUD Operations */
  // Read
  const currentUser = useGetCurrentUser();
  const { data: userSettings } = useGetUserSettings();
  const { users } = useTaggableUsers();
  // Update
  const { mutate: updateUserSettings } = useUpdateSetUserSettings();

  /* Helpers */
  const isAllView = selectedUserId === ALL_USERS_KEY;
  const isOwnView = !isAllView && selectedUserId === currentUser?.id;

  // Own header uses live settings (picker updates optimistically); another
  // staffer's header shows their saved color, read-only. All view: default.
  const selectedUser = users.find((u) => u.id === selectedUserId);
  const color =
    (isOwnView
      ? userSettings?.dashboardHeaderColor
      : // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        selectedUser?.userSettings?.dashboardHeaderColor) ||
    DashboardHeaderColor.LABORUP_ORIGINAL;
  const bannerClassName = dashboardHeaderColorToBannerClassName[color];

  const today = new Date();
  const weekday = today.toLocaleDateString("en-US", { weekday: "long" });
  const fullDate = dates.formatLongDate(today);

  /* Handlers */
  const handleColorChange = (next: DashboardHeaderColor) => {
    updateUserSettings({ dashboardHeaderColor: next });
  };

  const handleMinimize = () => {
    updateUserSettings({ dashboardHeaderMinimized: true });
  };

  const handleExpand = () => {
    updateUserSettings({ dashboardHeaderMinimized: false });
  };

  /* Render */
  // Both layers stay mounted and cross-fade while the banner height animates,
  // so expand/minimize reads as one smooth resize instead of a hard swap.
  const renderExpandedLayer = () => (
    <Row
      className={cn(
        LAYER_CLASS,
        isMinimized ? "pointer-events-none opacity-0" : "opacity-100",
      )}
    >
      <HousePennants className="absolute left-[62%] top-0 hidden -translate-x-1/2 xl:flex" />
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            className={cn(
              "pointer-events-none absolute right-[27px] top-2 z-10 h-8 w-8 opacity-0 transition-opacity",
              !isMinimized &&
                "focus-visible:pointer-events-auto focus-visible:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100",
              ICON_BUTTON_CLASS,
            )}
            onClick={handleMinimize}
            size="icon"
            type="button"
            variant="ghost"
          >
            <Minimize2Icon className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Minimize banner and stats</TooltipContent>
      </Tooltip>
      <DashboardHeaderBackgroundPicker
        className={cn(
          "absolute right-[27px] top-1/2 -translate-y-1/2",
          ICON_BUTTON_CLASS,
        )}
        color={color}
        disabled={!isOwnView}
        onColorChange={handleColorChange}
      />
      <Row className="h-full w-full min-w-0 items-center justify-between gap-4 pr-10">
        <Column className="min-w-0 shrink-0 gap-1">
          <Text
            className={cn("text-[22px] font-bold leading-7", HEADLINE_CLASS)}
          >
            Happy {weekday}, {currentUser?.firstName}
          </Text>
          <Text className={cn("text-xs font-medium", SUBTITLE_CLASS)}>
            {fullDate}
          </Text>
        </Column>
        <DashboardUserSelector
          onValueChange={onSelectedUserIdChange}
          triggerClassName={SELECTOR_TRIGGER_CLASS}
          value={selectedUserId}
        />
      </Row>
    </Row>
  );

  const renderMinimizedLayer = () => (
    <Row
      className={cn(
        LAYER_CLASS,
        isMinimized ? "opacity-100" : "pointer-events-none opacity-0",
      )}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            className={cn(
              "absolute right-[27px] top-2 z-10 h-8 w-8",
              ICON_BUTTON_CLASS,
            )}
            onClick={handleExpand}
            size="icon"
            type="button"
            variant="ghost"
          >
            <Maximize2Icon className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Expand banner and stats</TooltipContent>
      </Tooltip>
      <Row className="min-w-0 flex-1 items-center justify-between gap-4 pr-10">
        <Row className="min-w-0 items-center gap-2">
          <Text
            className={cn("truncate text-sm font-semibold", HEADLINE_CLASS)}
          >
            Happy {weekday}, {currentUser?.firstName}
          </Text>
          <Text
            className={cn(
              "hidden truncate text-xs font-medium sm:inline",
              SUBTITLE_CLASS,
            )}
          >
            {fullDate}
          </Text>
        </Row>
        <DashboardUserSelector
          onValueChange={onSelectedUserIdChange}
          triggerClassName={SELECTOR_TRIGGER_CLASS}
          value={selectedUserId}
        />
      </Row>
    </Row>
  );

  const render = () => (
    <Row
      className={cn(
        "group relative mx-6 shrink-0 overflow-hidden rounded-lg transition-[height] duration-300 ease-in-out",
        bannerClassName,
      )}
      style={{
        height: isMinimized
          ? DASHBOARD_HEADER_MINIMIZED_HEIGHT_PX
          : DASHBOARD_HEADER_HEIGHT_PX,
      }}
    >
      {renderExpandedLayer()}
      {renderMinimizedLayer()}
    </Row>
  );

  return render();
};
